// Global Instructions Rule Applied!
//
// Configuration → Pipeline Budget services: the admin read, add, update and
// remove of the GL accounts the Deal Pipeline → Budget tab lists under Cash
// (cfg.deal_pipeline_budget_accounts, owner 2026-11-03).
//
// Same fetch conventions as statementTypesConfigService. Every route is
// admin-gated at the backend AND inside the RPC. A refusal (a GL account
// already listed, a label too long) comes back 400 with the RPC's own
// sentence - a designed, user-facing outcome the panel shows in its Alert.
import { z } from 'zod'
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  PipelineBudgetAccountsAdmin,
  validatePipelineBudgetAccountsAdmin
} from '../../../state_manager/data_models/pipeline_budget_accounts_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

// The backend's error envelope key is `message` (handle_route_custom_error);
// `error` is the auth guard's.
const ErrorBodySchema = z.object({ message: z.string().optional(), error: z.string().optional() })

async function readJson(response: Response): Promise<unknown> {
  const body: unknown = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    const parsed = ErrorBodySchema.safeParse(body)
    const said = parsed.success ? parsed.data.message || parsed.data.error : undefined
    throw new Error(said || `Request failed (${response.status})`)
  }
  return body
}

/** The live rows in display order and the GL accounts a row can point at,
 *  validated before they are returned. */
export const fetchPipelineBudgetAccountsConfig = async (
  accessToken: string | null
): Promise<PipelineBudgetAccountsAdmin> => {
  const response = await fetch(buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINE_BUDGET_ACCOUNTS_CONFIG), {
    method: API_ROUTES.CONFIG.PIPELINE_BUDGET_ACCOUNTS_CONFIG.method,
    headers: authHeaders(accessToken)
  })
  return validatePipelineBudgetAccountsAdmin(await readJson(response))
}

export interface PipelineBudgetAccountFields {
  gl_account_id?: string
  label?: string | null
  display_order?: number
}

/** Add a row (accountId null) or update one. Only the supplied keys are
 *  touched - the RPC reads an absent key as "leave unchanged". Writes use the
 *  house request envelope { data: {...} }. */
export const savePipelineBudgetAccount = async (
  accessToken: string | null,
  accountId: string | null,
  fields: PipelineBudgetAccountFields
): Promise<void> => {
  const route = accountId
    ? API_ROUTES.CONFIG.PIPELINE_BUDGET_ACCOUNT_UPDATE
    : API_ROUTES.CONFIG.PIPELINE_BUDGET_ACCOUNT_CREATE
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, route, accountId ? { accountId } : undefined),
    {
      method: route.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

/** Remove a row: the account leaves the Budget tab and Net Cash. */
export const deletePipelineBudgetAccount = async (accessToken: string | null, accountId: string): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.PIPELINE_BUDGET_ACCOUNT_DELETE, { accountId }),
    {
      method: API_ROUTES.CONFIG.PIPELINE_BUDGET_ACCOUNT_DELETE.method,
      headers: authHeaders(accessToken)
    }
  )
  await readJson(response)
}
